import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertCircle, Check, ArrowLeft, ArrowRight, User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { fechaToInput, fechaToDisplay } from '../utils/fechaUtils';
import { TITULOS_VIGENTES, TITULOS_ANTERIORES } from '../constants/titulos';
import axios from 'axios';
import GoogleSignInButton from '../components/GoogleSignInButton';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GENEROS = [
  "Masculino",
  "Femenino", 
  "Feminidad trans/travesti",
  "Varón trans/masculinidad trans",
  "No binarie",
  "Otro"
];

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  
  // Detectar si viene desde auxiliares de justicia
  const fromAuxiliares = searchParams.get('from') === 'auxiliares';

  // Step 1: Verificar matrícula
  const [matricula, setMatricula] = useState('');
  const [documento, setDocumento] = useState('');
  const [trabajadorExistente, setTrabajadorExistente] = useState(null);

  // Step 2: Formulario completo (solo para usuarios nuevos)
  const [formData, setFormData] = useState({
    numero_matricula: '',
    documento: '',
    apellido: '',
    nombre: '',
    fecha_nacimiento: '',
    genero: '',
    titulo: '',
    email: '',
    celular: '',
    domicilio_real: { calle: '', numero: '', piso: '', depto: '', localidad: '' },
    domicilio_laboral: { calle: '', numero: '', piso: '', depto: '', localidad: '' },
    sin_empleo: false
  });

  // Step 3: Crear cuenta
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [trabajadorId, setTrabajadorId] = useState(null);


  const handleVerificarMatricula = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/trabajadores/verificar-existente`, {
        numero_matricula: matricula,
        documento: documento
      });

      if (response.data.existe) {
        // Verificar si ya tiene una cuenta creada
        if (response.data.ya_tiene_cuenta) {
          setError('Ya existe una cuenta registrada con esta matrícula y documento. Por favor, iniciá sesión o usá la opción "¿Olvidaste tu contraseña?" si no recordás tus credenciales.');
          setLoading(false);
          return;
        }
        
        setTrabajadorExistente(response.data);
        setTrabajadorId(response.data.trabajador_id);
        
        // Cargar datos existentes en el formulario
        if (response.data.trabajador_data) {
          const trab = response.data.trabajador_data;
          setFormData({
            numero_matricula: trab.numero_matricula || matricula,
            documento: trab.documento || documento,
            apellido: trab.apellido || '',
            nombre: trab.nombre || '',
            fecha_nacimiento: fechaToInput(trab.fecha_nacimiento) || '',
            genero: trab.genero || '',
            titulo: trab.titulo || '',
            email: trab.email || '',
            celular: trab.celular || '',
            domicilio_real: trab.domicilio_real || { calle: '', numero: '', piso: '', depto: '', localidad: '' },
            domicilio_laboral: trab.domicilio_laboral || { calle: '', numero: '', piso: '', depto: '', localidad: '' },
            sin_empleo: trab.sin_empleo || false
          });
        }
        // SIEMPRE ir al Step 2 para verificar/completar datos
        setStep(2);
      } else {
        // No existe en la base de datos - mostrar mensaje de error
        setError('Tus datos no se encuentran en el padrón del colegio. Por favor comunicate con el Colegio Distrital para regularizar tu situación.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al verificar');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Solo se permite si hay un trabajadorId (datos precargados)
      if (!trabajadorId) {
        setError('No se puede continuar sin datos precargados. Comunicate con el Colegio Distrital.');
        setStep(1);
        setLoading(false);
        return;
      }
      
      // Convertir fecha al formato dd/mm/yyyy para guardar
      const dataToSave = {
        ...formData,
        fecha_nacimiento: fechaToDisplay(formData.fecha_nacimiento)
      };
      
      await axios.post(`${API_URL}/trabajadores/completar-registro`, {
        trabajador_id: trabajadorId,
        numero_matricula: formData.numero_matricula,
        documento: formData.documento,
        data: dataToSave
      });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCrearCuenta = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, password, trabajadorId);
      // Redirigir según el origen
      if (fromAuxiliares) {
        navigate('/auxiliares-justicia/formulario');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear cuenta');
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateDomicilio = (tipo, field, value) => {
    setFormData(prev => ({
      ...prev,
      [tipo]: { ...prev[tipo], [field]: value }
    }));
  };

  const getStepStatus = (stepNum) => {
    if (step > stepNum) return 'completed';
    if (step === stepNum) return 'active';
    return '';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="/logo-colegio.png"
            alt="Colegio de Trabajadorxs Sociales"
            className="h-40 w-auto"
          />
        </div>

        {/* Progress Stepper */}
        <div className="stepper mb-8 px-4">
          <div className={`stepper-step ${getStepStatus(1)}`}>
            <div className="step-circle">
              {step > 1 ? <Check className="w-4 h-4" /> : '1'}
            </div>
            <span className="step-label">Verificar</span>
          </div>
          <div className={`stepper-step ${getStepStatus(2)}`}>
            <div className="step-circle">
              {step > 2 ? <Check className="w-4 h-4" /> : '2'}
            </div>
            <span className="step-label">Datos</span>
          </div>
          <div className={`stepper-step ${getStepStatus(3)}`}>
            <div className="step-circle">3</div>
            <span className="step-label">Cuenta</span>
          </div>
        </div>

        <Card className="shadow-lg">
          {/* Step 1: Verificar Matrícula */}
          {step === 1 && (
            <>
              <CardHeader>
                <CardTitle className="text-xl">Verificar Matrícula</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-700">
                    Ingresá tu número de matrícula y DNI para verificar si tus datos ya están precargadxs por el colegio.
                  </p>
                </div>

                <form onSubmit={handleVerificarMatricula} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="matricula">Número de Matrícula</Label>
                    <Input
                      id="matricula"
                      placeholder="1234"
                      value={matricula}
                      onChange={(e) => setMatricula(e.target.value.replace(/[.,\s]/g, ''))}
                      required
                      data-testid="register-matricula-input"
                    />
                    <p className="text-xs text-gray-500">Sin puntos ni comas</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="documento">DNI</Label>
                    <Input
                      id="documento"
                      placeholder="12345678"
                      value={documento}
                      onChange={(e) => setDocumento(e.target.value.replace(/[.,\s]/g, ''))}
                      required
                      data-testid="register-documento-input"
                    />
                    <p className="text-xs text-gray-500">Sin puntos ni comas</p>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="register-continue-btn">
                    {loading ? 'Verificando...' : 'Continuar'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>

                {/* Separador + botón oficial de Google */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-gray-500">o registrate con</span>
                  </div>
                </div>
                <GoogleSignInButton />

                <div className="mt-6 text-center">
                  <Link to={fromAuxiliares ? "/auxiliares-justicia/login" : "/login"} className="text-sm text-gray-500 hover:text-primary">
                    <ArrowLeft className="w-4 h-4 inline mr-1" />
                    Volver al login
                  </Link>
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Formulario Completo */}
          {step === 2 && (
            <>
              <CardHeader>
                <CardTitle className="text-xl">Verificá tus datos</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Mensaje para usuarios precargados */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-blue-800 font-medium mb-1">
                    ¡Hola {formData.nombre} {formData.apellido}!
                  </p>
                  <p className="text-sm text-blue-700">
                    Tus datos fueron precargados por el colegio. 
                    <strong> Verificá que la información esté actualizada</strong> y completá los campos faltantes antes de continuar.
                  </p>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-6">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  {/* Datos Básicos */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-primary" />
                      Datos Básicos
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Número de Matrícula *</Label>
                        <Input 
                          value={formData.numero_matricula} 
                          onChange={(e) => updateFormData('numero_matricula', e.target.value)} 
                          required 
                          readOnly={!!trabajadorId}
                          className={trabajadorId ? "bg-gray-100" : ""}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Documento *</Label>
                        <Input 
                          value={formData.documento} 
                          onChange={(e) => updateFormData('documento', e.target.value)} 
                          required 
                          readOnly={!!trabajadorId}
                          className={trabajadorId ? "bg-gray-100" : ""}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Apellido *</Label>
                        <Input value={formData.apellido} onChange={(e) => updateFormData('apellido', e.target.value)} required data-testid="register-apellido-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Nombre *</Label>
                        <Input value={formData.nombre} onChange={(e) => updateFormData('nombre', e.target.value)} required data-testid="register-nombre-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Fecha de Nacimiento *</Label>
                        <Input type="date" value={formData.fecha_nacimiento} onChange={(e) => updateFormData('fecha_nacimiento', e.target.value)} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Género</Label>
                        <select 
                          value={formData.genero} 
                          onChange={(e) => updateFormData('genero', e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <option value="">Seleccionar</option>
                          {GENEROS.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Título *</Label>
                        <select 
                          value={formData.titulo} 
                          onChange={(e) => updateFormData('titulo', e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          required
                        >
                          <option value="">Seleccionar</option>
                          <optgroup label="Titulaciones vigentes">
                            {TITULOS_VIGENTES.map(t => <option key={t} value={t}>{t}</option>)}
                          </optgroup>
                          <optgroup label="Titulaciones anteriores">
                            {TITULOS_ANTERIORES.map(t => <option key={t} value={t}>{t}</option>)}
                          </optgroup>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input type="email" value={formData.email} onChange={(e) => updateFormData('email', e.target.value)} required data-testid="register-email-input" />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Celular *</Label>
                        <Input value={formData.celular} onChange={(e) => updateFormData('celular', e.target.value)} required />
                      </div>
                    </div>
                  </div>

                  {/* Domicilio Real */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Domicilio Real</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label>Calle *</Label>
                        <Input value={formData.domicilio_real.calle} onChange={(e) => updateDomicilio('domicilio_real', 'calle', e.target.value)} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Número *</Label>
                        <Input value={formData.domicilio_real.numero} onChange={(e) => updateDomicilio('domicilio_real', 'numero', e.target.value)} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Piso</Label>
                        <Input value={formData.domicilio_real.piso} onChange={(e) => updateDomicilio('domicilio_real', 'piso', e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>Depto</Label>
                        <Input value={formData.domicilio_real.depto} onChange={(e) => updateDomicilio('domicilio_real', 'depto', e.target.value)} />
                      </div>
                      <div className="col-span-2 md:col-span-3 space-y-2">
                        <Label>Localidad *</Label>
                        <Input value={formData.domicilio_real.localidad} onChange={(e) => updateDomicilio('domicilio_real', 'localidad', e.target.value)} required />
                      </div>
                    </div>
                  </div>

                  {/* Domicilio Laboral */}
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Domicilio Laboral</h3>
                    
                    {/* Checkbox sin empleo */}
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.sin_empleo}
                          onChange={(e) => updateFormData('sin_empleo', e.target.checked)}
                          className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                          data-testid="sin-empleo-checkbox"
                        />
                        <span className="text-amber-800 font-medium">Me encuentro actualmente sin empleo</span>
                      </label>
                    </div>

                    {!formData.sin_empleo && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="col-span-2 space-y-2">
                          <Label>Calle *</Label>
                          <Input value={formData.domicilio_laboral.calle} onChange={(e) => updateDomicilio('domicilio_laboral', 'calle', e.target.value)} required={!formData.sin_empleo} />
                        </div>
                        <div className="space-y-2">
                          <Label>Número *</Label>
                          <Input value={formData.domicilio_laboral.numero} onChange={(e) => updateDomicilio('domicilio_laboral', 'numero', e.target.value)} required={!formData.sin_empleo} />
                        </div>
                        <div className="space-y-2">
                          <Label>Piso</Label>
                          <Input value={formData.domicilio_laboral.piso} onChange={(e) => updateDomicilio('domicilio_laboral', 'piso', e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Depto</Label>
                          <Input value={formData.domicilio_laboral.depto} onChange={(e) => updateDomicilio('domicilio_laboral', 'depto', e.target.value)} />
                        </div>
                        <div className="col-span-2 md:col-span-3 space-y-2">
                          <Label>Localidad *</Label>
                          <Input value={formData.domicilio_laboral.localidad} onChange={(e) => updateDomicilio('domicilio_laboral', 'localidad', e.target.value)} required={!formData.sin_empleo} />
                        </div>
                      </div>
                    )}

                    {formData.sin_empleo && (
                      <p className="text-sm text-gray-500 italic">Los campos de domicilio laboral no son requeridos cuando no tenés empleo actualmente.</p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Volver
                    </Button>
                    <Button type="submit" className="flex-1" disabled={loading} data-testid="register-save-data-btn">
                      {loading ? 'Guardando...' : (trabajadorExistente ? 'Confirmar y Continuar' : 'Guardar Datos')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </>
          )}

          {/* Step 3: Crear Cuenta */}
          {step === 3 && (
            <>
              <CardHeader>
                <CardTitle className="text-xl">Crear Cuenta</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Mensaje personalizado para usuarios con datos precargados */}
                {trabajadorExistente ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-testid="preloaded-welcome-message">
                    <p className="text-sm text-blue-700">
                      <strong>¡Hola {formData.nombre}!</strong> Tus datos ya fueron precargados por el colegio. 
                      Solo necesitás crear una contraseña para acceder a tu cuenta.
                    </p>
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                    <Check className="w-5 h-5 text-emerald-600" />
                    <p className="text-sm text-emerald-700">
                      <strong>Datos guardados.</strong> Ahora creá tu cuenta de acceso.
                    </p>
                  </div>
                )}

                <form onSubmit={handleCrearCuenta} className="space-y-4">
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="input-with-icon">
                      <Mail className="icon-left w-4 h-4" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        readOnly
                        className="pl-10 bg-gray-50"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña</Label>
                    <div className="input-with-icon has-right-icon">
                      <Lock className="icon-left w-4 h-4" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Mínimo 6 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="pl-10 pr-10"
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="icon-right cursor-pointer hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                    <div className="input-with-icon">
                      <Lock className="icon-left w-4 h-4" />
                      <Input
                        id="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Repetí tu contraseña"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="pl-10"
                        data-testid="register-confirm-password-input"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading} data-testid="register-create-account-btn">
                    {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
